/**
 * SEO Cross-Tool Calibration Constants (Story 20.9 — AC #2, #8)
 *
 * Why these are static module-level constants (NOT API-sourced):
 *   Round 4 Red Team Attack #4 — the cross-SEO widget must never interpret
 *   per-tool integration metadata as label text. Sourcing display names or
 *   the calibration footnote from `integration.metadata.label` would let an
 *   attacker who controls integration metadata XSS into the cross-SEO
 *   widget render path. HTML-escape at render time is still applied
 *   (defense-in-depth) but the primary defense is "never source these from
 *   user-influenced metadata".
 *
 * Why the calibration footnote is non-negotiable:
 *   Round 1 Sales Engineer + Round 4 Red Team Attack #2 — without the
 *   footnote, agency clients misread "DR 56 vs DA 50" as the site failing
 *   on Moz when it's just scale calibration. The footnote is the
 *   single-sentence-explainer that makes the cross-tool comparison
 *   interpretable rather than alarming.
 *
 * Calibration source citations (verified 2026-05-13):
 *   - xamsor.com/blog/ahrefs-dr-semrush-as-moz-da-majestic-tf-comparison
 *   - backlinkcrm.io/moz-da-vs-ahrefs-dr-vs-semrush-as
 *   - outrank.so/blog/difference-between-moz-and-ahrefs-domain-ranking
 *
 * @see `_bmad-output/implementation-artifacts/20-9-seo-unified-metrics-widget.md` AC #8
 */

/**
 * The single-sentence calibration footnote rendered below every cross-SEO
 * widget. Story 20.9 AC #8 — exact text MUST match (E2E spec asserts this).
 *
 * Round 2 Code Review M-R2-1 / M2-Edge fix — reworded from the
 * earlier-draft "AS runs ~15-20% lower than DA" prescriptive scale
 * relationship. The earlier text directly contradicted the GreenLife
 * demo seed values (AS=49→52 vs DA=47→50, where AS > DA every month) —
 * agency-client users reading the footnote AND the chart side-by-side
 * would see contradictory information. The new wording acknowledges
 * per-site variance ("calibration varies per site") and surfaces the
 * canonical "what's your DA?" industry convention without committing
 * to a specific cross-tool ordering.
 *
 * `as const` so any code attempting to mutate the export is rejected at
 * compile-time. The actual XSS defense at render time is the existing
 * `htmlEscape` helper from Story 20.3.
 */
export const SEO_CROSS_TOOL_CALIBRATION_FOOTNOTE =
  `Different SEO tools score authority on different scales — Ahrefs DR, Semrush AS, and Moz DA each use proprietary models, so identical sites typically receive different scores from each tool. Calibration varies per site. Industry convention: when a journalist or publisher asks "what's your DA?", they mean Moz DA.` as const;

/**
 * The 3-tool display names rendered as bar-chart x-axis labels, line-chart
 * legend entries, and KPI card titles. Round 4 Red Team Attack #4 — NEVER
 * source from API metadata; this static record is the single source of
 * truth.
 *
 * Type-narrowed to the 3 known platform keys so TypeScript catches typos
 * and any attempt to look up an attacker-controlled key (e.g.
 * `SEO_TOOL_DISPLAY_NAMES[userInput]`) at compile time.
 */
export const SEO_TOOL_DISPLAY_NAMES: Record<
  "ahrefs" | "semrush" | "moz",
  string
> = {
  ahrefs: "Ahrefs DR",
  semrush: "Semrush AS",
  moz: "Moz DA",
};

/**
 * Per-tool brand colors for cross-SEO bar / line chart rendering. Sourced
 * from each vendor's canonical brand-color style guide:
 *   - Ahrefs: `#FF7700` — Ahrefs marketing site primary orange
 *   - Semrush: `#FF642D` — Semrush brand guideline canonical orange
 *     (Round 2 Code Review M3-Edge fix corrected from the close-but-
 *     wrong `#F95F2D` initial pass)
 *   - Moz: `#0099FF` — Moz marketing site primary blue
 *
 * Round 4 Red Team Attack #4 hardens these as the single source of truth
 * for cross-SEO widget rendering; getting them visibly wrong dilutes the
 * "we know SEO" credibility the cross-tool widget is meant to establish.
 */
export const SEO_TOOL_BRAND_COLORS: Record<
  "ahrefs" | "semrush" | "moz",
  string
> = {
  ahrefs: "#FF7700",
  semrush: "#FF642D",
  moz: "#0099FF",
};
